import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiShare2, FiMessageCircle, FiEye } = FiIcons;

const CollaborationPanel = ({ sessionId, collaborators, currentStep }) => {
  const [showChat, setShowChat] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  const mockCollaborators = [
    { id: 1, name: 'Alice Johnson', email: 'alice@example.com', status: 'online', avatar: 'AJ' },
    { id: 2, name: 'Bob Smith', email: 'bob@example.com', status: 'viewing', avatar: 'BS' }
  ];

  const sendMessage = () => {
    if (!newMessage.trim()) return;
    
    const message = {
      id: Date.now(),
      user: 'You',
      content: newMessage,
      timestamp: new Date().toLocaleTimeString()
    };
    
    setMessages(prev => [...prev, message]);
    setNewMessage('');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-effect rounded-xl p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <SafeIcon icon={FiUsers} />
          Collaboration
        </h3>
        <button className="text-blue-600 hover:text-blue-700 transition-colors">
          <SafeIcon icon={FiShare2} />
        </button>
      </div>

      {/* Collaborators List */}
      <div className="space-y-3 mb-6">
        <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white text-sm font-medium">
            Y
          </div>
          <div className="flex-1">
            <p className="font-medium text-slate-800">You (Host)</p>
            <p className="text-sm text-slate-600">Step {currentStep}</p>
          </div>
          <div className="w-2 h-2 rounded-full bg-green-500 collaboration-indicator"></div>
        </div>

        {mockCollaborators.map((collaborator) => (
          <div key={collaborator.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
              {collaborator.avatar}
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-800">{collaborator.name}</p>
              <p className="text-sm text-slate-600 flex items-center gap-1">
                <SafeIcon icon={FiEye} className="text-xs" />
                {collaborator.status}
              </p>
            </div>
            <div className={`w-2 h-2 rounded-full ${
              collaborator.status === 'online' ? 'bg-green-500' : 'bg-yellow-500'
            }`}></div>
          </div>
        ))}
      </div>

      {/* Chat Toggle */}
      <button
        onClick={() => setShowChat(!showChat)}
        className="w-full flex items-center justify-center gap-2 p-3 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
      >
        <SafeIcon icon={FiMessageCircle} />
        {showChat ? 'Hide Chat' : 'Show Chat'}
      </button>

      {/* Chat Panel */}
      {showChat && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          className="mt-4 border-t border-slate-200 pt-4"
        >
          <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
            {messages.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No messages yet</p>
            ) : (
              messages.map((message) => (
                <div key={message.id} className="p-2 bg-slate-50 rounded text-sm">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-slate-800">{message.user}</span>
                    <span className="text-xs text-slate-500">{message.timestamp}</span>
                  </div>
                  <p className="text-slate-700">{message.content}</p>
                </div>
              ))
            )}
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Type a message..."
              className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={sendMessage}
              className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
            >
              Send
            </button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default CollaborationPanel;